// src/pages/PreInterview.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import api from "../api"; // 共通axiosインスタンス
import CustomButton from "../components/CustomButton";
import { Form, Checkbox, FormGroup, TextArea } from "@carbon/react";
import dayjs from "dayjs";

const SHORT_LIMIT = 100;
const LONG_LIMIT = 200;

/** 短文入力（改行禁止・文字数カウント） */
function CountedShortText({ id, labelText, placeholder, maxLength = SHORT_LIMIT, value, onChange, rows = 2 }) {
  const counterId = `${id}-counter`;

  return (
    <div className="counted-textarea">
      <TextArea
        id={id}
        labelText={labelText}
        placeholder={placeholder}
        rows={rows}
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value.slice(0, maxLength))}
        aria-describedby={counterId}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault(); // 改行禁止
        }}
      />
      <div
        id={counterId}
        aria-live="polite"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 2,
          fontSize: 12,
          lineHeight: 1,
          color: "#6f6f6f",
        }}
      >
        {value.length}/{maxLength} 文字
      </div>
    </div>
  );
}

/** 長文入力（改行OK・文字数カウント） */
function CountedTextArea({ id, labelText, placeholder, rows = 4, maxLength = LONG_LIMIT, value, onChange }) {
  const counterId = `${id}-counter`;

  return (
    <div className="counted-textarea">
      <TextArea
        id={id}
        labelText={labelText}
        placeholder={placeholder}
        rows={rows}
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value.slice(0, maxLength))}
        aria-describedby={counterId}
      />
      <div
        id={counterId}
        aria-live="polite"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 2,
          fontSize: 12,
          lineHeight: 1,
          color: "#6f6f6f",
        }}
      >
        {value.length}/{maxLength} 文字
      </div>
    </div>
  );
}

/** ラジオボタングループ（9択質問） */
function RadioButtonGroup({ id, labelText, options, value, onChange }) {
  return (
    <fieldset
      className="radio-button-group"
      style={{
        marginBottom: "1.5rem",
        border: "none",
        padding: 0,
      }}
    >
      <legend
        style={{
          display: "flex",
          justifyContent: "flex-start",
          marginTop: 2,
          fontSize: 12,
          lineHeight: 1,
          color: "#6f6f6f",
        }}
      >
        {labelText}
      </legend>

      {options.map((option, index) => (
        <div key={index} style={{ marginBottom: "0.25rem" }}>
          <label
            style={{
              display: "flex",
              justifyContent: "flex-start",
              marginTop: 4,
              fontSize: 12,
              lineHeight: 2,
              color: "#161616",
            }}
          >
            <input
              type="radio"
              name={id}
              value={option.value}
              checked={value === option.value}
              onChange={(e) => onChange(e.target.value)}
              style={{
                marginRight: "0.5rem",
                accentColor: "#0f62fe", // Carbonのプライマリカラー
              }}
            />
            {option.label}
          </label>
        </div>
      ))}
    </fieldset>
  );
}


export default function PreInterview() {
  const navigate = useNavigate();
  const { user } = useAuth(); // ログイン中ユーザー
  const [nonAmbulatory, setNonAmbulatory] = useState(false);

  // フォーム全体をstate管理
  const [form, setForm] = useState({
    job: "",
    jobDetail: "",
    household: "",
    exercise: "",
    holiday: "",
    happy: "",
    symptom: "",
    since: "",
    concern: "",
    radioQuestion: "",
  });

  const radioOptions = [
    { value: "0", label: "現在は症状は全くない" },
    { value: "1", label: "軽い症状があるが、日常生活には支障が無い" },
    { value: "2", label: "手や視覚などに少し制限がある" },
    { value: "3", label: "歩行が少し不自由だが補助具は不要" },
    { value: "4", label: "時々杖や片手の支えが必要になる" },
    { value: "5", label: "常に杖や片手の支えが必要になる" },
    { value: "6", label: "両手の支え（両杖・歩行器など）が必要" },
    { value: "7", label: "車いすなどを使わなければいけない" },
  ];

  // 初期データ読み込み
  useEffect(() => {
    if (!user || user.role !== "patient") return;

    const fetchData = async () => {
      try {
        const res = await api.get(`/v1/patient/recent_status/${user.id}`);
        console.log("GET status:", res.status);
        setForm({
          job: res.data.main_activities || "",
          jobDetail: res.data.activities_detail || "",
          household: res.data.living_with || "",
          exercise: res.data.exercise || "",
          holiday: res.data.spend_dayoff || "",
          happy: res.data.you_made_happy || "",
          symptom: res.data.concerning_symptoms || "",
          since: res.data.symptoms_duration || "",
          concern: res.data.concerns_or_questions || "",
        });
      } catch (err) {
        console.error("API error:", err);
      }
    };

    fetchData();
  }, [user]);

  // POSTしてAI面談へ
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      research_id: user.id,
      main_activities: form.job,
      activities_detail: form.jobDetail,
      living_with: form.household,
      exercise: form.exercise,
      spend_dayoff: form.holiday,
      you_made_happy: form.happy,
      concerning_symptoms: form.symptom,
      symptoms_duration: form.since,
      concerns_or_questions: form.concern,
      radio_question: form.radioQuestion,
      // date_of_answer: dayjs().format("YYYY-MM-DD"),
      date_of_answer: dayjs().tz("Asia/Tokyo").format("YYYY-MM-DD"),
    };

    try {
      const res = await api.post("/v1/patient/recent_status", payload);
      if (res.status === 200) {
        console.log("PreInterview 保存成功:", res.data);
        navigate("/ai-interview", { state: { nonAmbulatory, preData: payload } });
      }
    } catch (err) {
      console.error("PreInterview 保存失敗:", err.message);
    }
  };

  const formStyle = {
    maxWidth: "700px",
    margin: "auto",
    padding: "2rem",
    background: "#fff",
    borderRadius: "4px",
    boxShadow: "0 0 10px rgba(0,0,0,0.05)",
  };

  return (
    <>
      <div style={{ background: "#f4f4f4", minHeight: "100vh", padding: "1rem" }}>
        <div className="start-ai-chat-form" style={formStyle}>
          <h4 style={{ marginTop: 0, marginBottom: "1.5rem" }}>以下の質問に回答してください。</h4>
          <p style={{ fontSize: "0.875rem", marginBottom: "1.5rem" }}>
            ※前回の情報が表示されています。更新がない場合は、そのまま送信してください。
          </p>

          <Form onSubmit={handleSubmit}>
            <CountedShortText id="job" labelText="1. 職業を含む日中の主な活動"
              placeholder="例：介護職、エンジニア、家事、配送業、など"
              value={form.job} onChange={(v) => setForm({ ...form, job: v })} />

            <CountedTextArea id="jobDetail" labelText="2. 主な作業内容"
              placeholder="例（介護職の場合）：利用者の移動・歩行の介助、食事や水分補給のサポート、入浴介助、排泄介助、バイタル測定、レクリエーション活動の補助、PCを使用してケア記録の記入"
              rows={5} value={form.jobDetail} onChange={(v) => setForm({ ...form, jobDetail: v })} />

            <CountedShortText id="household" labelText="3. 同居している人（ペットを含め）"
              placeholder="例：夫、娘（5歳と2歳）、猫、単身、一人暮らし、など"
              value={form.household} onChange={(v) => setForm({ ...form, household: v })} />

            <CountedTextArea id="exercise" labelText="4. 運動"
              placeholder="例：定期的にヨガを行っているが、最近は暑さと症状のせいで休みがち"
              rows={4} value={form.exercise} onChange={(v) => setForm({ ...form, exercise: v })} />

            <CountedTextArea id="holiday" labelText="5. 好きな休日の過ごし方"
              placeholder="例：旅行や友人と食事に行ったりする"
              rows={4} value={form.holiday} onChange={(v) => setForm({ ...form, holiday: v })} />

            <CountedTextArea id="happy" labelText="6. 最近の嬉しかったこと"
              placeholder="例：近所にスーパーができた、きれいな花を見つけた"
              rows={3} value={form.happy} onChange={(v) => setForm({ ...form, happy: v })} />

            <CountedShortText id="symptom" labelText="7. 現在の症状"
              placeholder="例：右手の痺れ"
              value={form.symptom} onChange={(v) => setForm({ ...form, symptom: v })} />

            <CountedShortText id="since" labelText="8. 現在の症状はいつからありますか？"
              placeholder="例：約3週間前から"
              value={form.since} onChange={(v) => setForm({ ...form, since: v })} />

            <CountedTextArea id="concern" labelText="9. 不安・医師に相談したいこと"
              placeholder="例：いつまでこの治療を続けなくてはいけないか、症状が出にくくするためのアドバイス"
              rows={4} value={form.concern} onChange={(v) => setForm({ ...form, concern: v })} />

            <RadioButtonGroup id="radioQuestion" labelText="10. ご自身の病状について、最も当てはまるものを選んでください。"
              options={radioOptions}
              value={form.radioQuestion} onChange={(v) => setForm({ ...form, radioQuestion: v })} />

            <FormGroup legendText={
              <>11. 全く歩くことができない場合は、このボックスにチェックを入れてください。<br />
              （1～3問目（歩行機能に関する質問）がスキップされます）</>}>
              <Checkbox id="nonAmbulatory" labelText="該当する"
                checked={nonAmbulatory}
                onChange={(e) => setNonAmbulatory(e.target.checked)} />
            </FormGroup>

            <CustomButton kind="primary" type="submit" style={{ marginTop: "1rem" }}>
              AI問診へ
            </CustomButton>
          </Form>
        </div>
      </div>

      {/* スコープドCSS */}
      <style>{`
        .start-ai-chat-form .cds--form-item { margin-bottom: 24px; }
        .start-ai-chat-form .cds--text-area__wrapper textarea { resize: vertical; }
        .start-ai-chat-form .counted-input,
        .start-ai-chat-form .counted-textarea { margin-bottom: 24px; }
        .start-ai-chat-form .counted-input > .cds--form-item,
        .start-ai-chat-form .counted-textarea > .cds--form-item { margin-bottom: 4px !important; }
        .start-ai-chat-form .counted-textarea .cds--text-area__wrapper { margin-bottom: 0 !important; }
        .start-ai-chat-form form .cds--form-item:last-of-type { margin-bottom: 32px; }
      `}</style>
    </>
  );
}

// isolatedModules対策
export {};
